import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, createTransform } from 'redux-persist';
import AsyncStorage from '@react-native-async-storage/async-storage';

import sessionReducer from './reducers/session';
import authenticationReducer from './reducers/authentication';
import accountsReducer from './reducers/accounts';
import projectsReducer from './reducers/projects';
import promptsReducer from './reducers/prompts';
import loaderReducer from './reducers/loader';
import notificationReducer from './reducers/notification';
import accountAssistantReducer from './reducers/accountAssistant';
import blocksReducer from './reducers/blocks';
import calendarReducer from './reducers/calendar';
import fastenReducer from './reducers/fasten';
import responsesReducer from './reducers/responses';
import projectCreationDraftReducer from './reducers/projectCreationDraft';
import medicationsReducer from './reducers/medications';
import contactsReducer from './reducers/contacts';
import notesReducer from './reducers/notes';
import noteTemplatesReducer from './reducers/noteTemplates';
import documentsReducer from './reducers/documents';
import benefitsReducer from './reducers/benefits';
import vaultReducer from './reducers/vault';
import proposedActionsReducer from './reducers/proposedActions';
import messagesReducer from './reducers/messages';
import remindersReducer from './reducers/reminders';
import snapshotsReducer from './reducers/snapshots';
import partnersReducer from './reducers/partners';
import briefingsReducer from './reducers/briefings';
import composerReducer from './reducers/composer';
import featureFlagsReducer from './reducers/featureFlags';

const rootReducer = combineReducers({
  session: sessionReducer,
  authentication: authenticationReducer,
  accounts: accountsReducer,
  projects: projectsReducer,
  prompts: promptsReducer,
  loader: loaderReducer,
  notification: notificationReducer,
  accountAssistant: accountAssistantReducer,
  blocks: blocksReducer,
  calendar: calendarReducer,
  fasten: fastenReducer,
  responses: responsesReducer,
  projectCreationDraft: projectCreationDraftReducer,
  medications: medicationsReducer,
  contacts: contactsReducer,
  notes: notesReducer,
  noteTemplates: noteTemplatesReducer,
  documents: documentsReducer,
  benefits: benefitsReducer,
  vault: vaultReducer,
  proposedActions: proposedActionsReducer,
  messages: messagesReducer,
  reminders: remindersReducer,
  snapshots: snapshotsReducer,
  partners: partnersReducer,
  briefings: briefingsReducer,
  composer: composerReducer,
  featureFlags: featureFlagsReducer,
});

// Strip the lock state from `session` on both write and rehydrate. If we
// wrote `locked: { show: true }` to disk, every reload (web refresh,
// Electron renderer restart, native cold-launch) would come up locked
// regardless of whether the user actually authenticated last time. We strip
// outbound too so users with already-persisted locked state from before this
// guard land unlocked on first run after the upgrade. The canonical lock
// signals at boot are the bootstrap path's passkey lock and the Electron
// main idle / system-lock IPC — not the persisted UI flag.
const STRIPPED_LOCK = { show: false, inactive: false };
const sessionPersistTransform = createTransform(
  (inboundState: any) => {
    if (!inboundState || typeof inboundState !== 'object') return inboundState;
    return { ...inboundState, locked: STRIPPED_LOCK };
  },
  (outboundState: any) => {
    if (!outboundState || typeof outboundState !== 'object') return outboundState;
    return { ...outboundState, locked: STRIPPED_LOCK };
  },
  { whitelist: ['session'] },
);

const persistConfig = {
  key: 'root',
  storage: AsyncStorage,
  // Persisted: auth credentials, session token, theme preference
  whitelist: ['session', 'authentication'],
  transforms: [sessionPersistTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        warnAfter: 500,
      },
      // Project payloads are large — this check is dev-only and already off in production
      immutableCheck: false,
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
